/**
 * wishlist-form.js
 *
 * Custom web components that mirror the ATC system (product-form.js) but
 * persist data to localStorage instead of POSTing to Shopify's cart API.
 *
 * Registers two custom elements:
 *   <save-to-wishlist-component>   – manages the heart button (mirrors AddToCartComponent)
 *   <wishlist-form-component>      – manages the outer container (mirrors ProductFormComponent)
 *
 * Storage schema (localStorage key: 'theme_wishlist'):
 *   {
 *     items: [
 *       {
 *         productId:     string,   // product.id
 *         variantId:     string,   // selected_or_first_available_variant.id
 *         productHandle: string,   // product.handle
 *         productTitle:  string,   // product.title
 *         variantTitle:  string,   // variant.title (or '' for default)
 *         price:         number,   // variant.price in cents
 *         imageUrl:      string,   // variant preview image URL
 *         productUrl:    string,   // product.url
 *         savedAt:       number,   // Date.now() timestamp
 *       }
 *     ]
 *   }
 */

import { Component } from '@theme/component';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

// ─── Storage helpers ─────────────────────────────────────────────────────────

const STORAGE_KEY = 'theme_wishlist';

/**
 * Reads the current wishlist from localStorage.
 * @returns {{ items: Array<Object> }}
 */
function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [] };
    return JSON.parse(raw);
  } catch {
    return { items: [] };
  }
}

/**
 * Writes the wishlist back to localStorage and dispatches a
 * WishlistUpdateEvent so any listening components can react.
 * @param {{ items: Array<Object> }} wishlist
 */
function writeWishlist(wishlist) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlist));
    document.dispatchEvent(
      new CustomEvent('wishlist:update', {
        bubbles: true,
        detail: { resource: wishlist },
      })
    );
  } catch (e) {
    console.error('[Wishlist] Could not write to localStorage', e);
  }
}

/**
 * Returns true if the given variantId is currently in the wishlist.
 * @param {string} variantId
 * @returns {boolean}
 */
export function isInWishlist(variantId) {
  const { items } = readWishlist();
  return items.some((item) => item.variantId === String(variantId));
}

/**
 * Adds an item to the wishlist. No-ops if the variant is already saved.
 * @param {Object} item
 */
function addToWishlist(item) {
  const wishlist = readWishlist();
  const alreadySaved = wishlist.items.some((i) => i.variantId === String(item.variantId));
  if (alreadySaved) return;
  wishlist.items.push({ ...item, variantId: String(item.variantId), productId: String(item.productId), savedAt: Date.now() });
  writeWishlist(wishlist);
}

/**
 * Removes an item from the wishlist by variantId.
 * @param {string} variantId
 */
function removeFromWishlist(variantId) {
  const wishlist = readWishlist();
  wishlist.items = wishlist.items.filter((i) => i.variantId !== String(variantId));
  writeWishlist(wishlist);
}

// ─── SaveToWishlistComponent ──────────────────────────────────────────────────
// Mirrors AddToCartComponent from product-form.js.
// Wraps the <button> element and owns the heart-fill animation.

/**
 * @typedef {object} SaveToWishlistRefs
 * @property {HTMLButtonElement} saveToWishlistButton
 */

export class SaveToWishlistComponent extends Component {
  requiredRefs = ['saveToWishlistButton'];

  /** @type {number[]} */
  #resetTimeouts = [];

  connectedCallback() {
    super.connectedCallback();
    // Sync initial pressed state from localStorage on mount
    this.#syncPressedState();

    // Re-sync whenever another tab or component changes the wishlist
    window.addEventListener('storage', this.#onStorageChange);
    document.addEventListener('wishlist:update', this.#onWishlistUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resetTimeouts.forEach((id) => clearTimeout(id));
    window.removeEventListener('storage', this.#onStorageChange);
    document.removeEventListener('wishlist:update', this.#onWishlistUpdate);
  }

  // ── Public API (called by WishlistFormComponent) ──────────────────────────

  /** Disables the button (e.g. during variant transitions) */
  disable() {
    this.refs.saveToWishlistButton.disabled = true;
  }

  /** Re-enables the button */
  enable() {
    this.refs.saveToWishlistButton.disabled = false;
  }

  /**
   * Sets the aria-pressed state and data-saved attribute to reflect
   * whether the current variant is in the wishlist.
   * @param {boolean} saved
   */
  setSaved(saved) {
    const btn = this.refs.saveToWishlistButton;
    btn.setAttribute('aria-pressed', String(saved));
    btn.dataset.saved = String(saved);
  }

  /**
   * Plays the heart-fill animation (mirrors animateAddToCart).
   * Sets data-saved="true" → CSS transition fills the heart → resets.
   */
  animateSave = async function () {
    const { saveToWishlistButton } = this.refs;

    this.#resetTimeouts.forEach((id) => clearTimeout(id));
    this.#resetTimeouts = [];

    // Mark as just-saved so CSS can play the burst/fill animation
    saveToWishlistButton.dataset.justSaved = 'true';

    await yieldToMainThread();
    await onAnimationEnd(saveToWishlistButton);

    const timeoutId = setTimeout(() => {
      saveToWishlistButton.removeAttribute('data-just-saved');
      const idx = this.#resetTimeouts.indexOf(timeoutId);
      if (idx > -1) this.#resetTimeouts.splice(idx, 1);
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };

  // ── Private ───────────────────────────────────────────────────────────────

  /** Reads current variantId from closest wishlist-form-component */
  #getCurrentVariantId() {
    const form = /** @type {WishlistFormComponent | null} */ (this.closest('wishlist-form-component'));
    return form?.getCurrentVariantId?.() ?? null;
  }

  #syncPressedState = () => {
    const variantId = this.#getCurrentVariantId();
    if (!variantId) return;
    this.setSaved(isInWishlist(variantId));
  };

  #onStorageChange = (/** @type {StorageEvent} */ e) => {
    if (e.key !== STORAGE_KEY) return;
    this.#syncPressedState();
  };

  #onWishlistUpdate = () => {
    this.#syncPressedState();
  };
}

if (!customElements.get('save-to-wishlist-component')) {
  customElements.define('save-to-wishlist-component', SaveToWishlistComponent);
}

// ─── WishlistFormComponent ────────────────────────────────────────────────────
// Mirrors ProductFormComponent from product-form.js.
// Owns click handling, localStorage write, and variant-change reactivity.

/**
 * @typedef {object} WishlistFormRefs
 * @property {HTMLInputElement}              variantId                    – hidden input with current variant ID
 * @property {SaveToWishlistComponent|undefined} saveToWishlistButtonContainer – the button component
 * @property {HTMLElement}                   liveRegion                   – aria-live region
 */

class WishlistFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Returns the currently selected variant ID.
   * @returns {string | null}
   */
  getCurrentVariantId() {
    return this.refs.variantId?.value ?? null;
  }

  /**
   * Handles the click on the save-to-wishlist button.
   * Toggles the item in localStorage and plays the animation.
   */
  handleClick() {
    const variantId = this.getCurrentVariantId();
    if (!variantId) return;

    const container = this.refs.saveToWishlistButtonContainer;
    const currentlySaved = isInWishlist(variantId);

    if (currentlySaved) {
      // ── Remove from wishlist ──
      removeFromWishlist(variantId);
      container?.setSaved(false);
      this.#setLiveRegionText(
        this.dataset.removeText || 'Removed from wishlist'
      );
    } else {
      // ── Add to wishlist ──
      const item = this.#buildItem(variantId);
      addToWishlist(item);
      container?.setSaved(true);
      container?.animateSave?.();
      this.#setLiveRegionText(
        this.dataset.addText || 'Saved to wishlist'
      );
    }

    // Clear the live region announcement after a beat
    setTimeout(() => this.#clearLiveRegionText(), 5000);
  }

  // ── Private ───────────────────────────────────────────────────────────────

  /**
   * Builds the wishlist item payload from data attributes set by Liquid.
   * @param {string} variantId
   * @returns {Object}
   */
  #buildItem(variantId) {
    return {
      productId:     this.dataset.productId     ?? '',
      variantId:     variantId,
      productHandle: this.dataset.productHandle ?? '',
      productTitle:  this.dataset.productTitle  ?? '',
      variantTitle:  this.dataset.variantTitle  ?? '',
      price:         Number(this.dataset.variantPrice ?? 0),
      imageUrl:      this.dataset.variantImageUrl ?? '',
      productUrl:    this.dataset.productUrl    ?? '',
    };
  }

  /** @param {string} text */
  #setLiveRegionText(text) {
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = '';
  }

  /**
   * Reacts to variant changes — updates the hidden variantId input,
   * syncs the button pressed state, and updates the data attributes
   * used by #buildItem so the correct variant gets saved.
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Update the tracked variant ID
    if (this.refs.variantId) {
      this.refs.variantId.value = variant?.id ?? '';
    }

    // Update data attributes from the new variant
    if (variant) {
      this.dataset.variantTitle    = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      this.dataset.variantPrice    = String(variant.price ?? 0);
      this.dataset.variantImageUrl = variant.featured_media?.preview_image?.src
        ? variant.featured_media.preview_image.src + '&width=100'
        : (this.dataset.variantImageUrl ?? '');
    }

    // Sync button state to the newly selected variant
    const container = this.refs.saveToWishlistButtonContainer;
    if (container && variant?.id) {
      container.setSaved(isInWishlist(String(variant.id)));
      // Re-enable the button if it was disabled during a transition
      if (variant.available !== false) {
        container.enable();
      }
    }
  };
}

if (!customElements.get('wishlist-form-component')) {
  customElements.define('wishlist-form-component', WishlistFormComponent);
}
